import React, { useEffect, useState } from 'react';
import { Alert, Box, Stack, SxProps, Theme, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { CHART_PALETTE, PREMIUM_COLORS, tokens } from '../../../theme';
import { StatTile, SectionCard as UiSectionCard, LoadingState } from '../../../components/ui';
import { formatMoney, formatNumber as formatNumberBase, formatPercent } from '../../../utils/format';

// Kolory kategorii na wykresach — ta sama paleta co w calej aplikacji.
export const CHART_COLORS: string[] = CHART_PALETTE;

// Zostawione dla zgodnosci wstecz; ekrany statystyk nie uzywaja juz gradientow.
export const GRADIENTS = PREMIUM_COLORS.gradients;

/** Kwota w zlotych: domyslnie zaokraglona do pelnych zlotych, `decimals` wymusza grosze. */
export const formatPLN = (n: number, decimals = false) => formatMoney(n || 0, { decimals: decimals ? 2 : 0 });
export const formatNumber = (n: number) => formatNumberBase(n || 0);
export const formatPct = (n: number) => formatPercent(Math.round(n || 0));

export function daysBetween(dateFrom: string, dateTo: string): number {
  const from = new Date(dateFrom).getTime();
  const to = new Date(dateTo).getTime();
  return Math.max(1, Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1);
}

// Long ranges roll up to weekly buckets so charts stay legible.
export function pickGranularity(dateFrom: string, dateTo: string): 'day' | 'week' {
  return daysBetween(dateFrom, dateTo) > 70 ? 'week' : 'day';
}

/** Siatka kafelkow z liczbami: 2 kolumny na telefonie, `columns` od md. */
export const StatTileGrid: React.FC<{ columns?: number; children: React.ReactNode; sx?: SxProps<Theme> }> = ({
  columns = 4,
  children,
  sx,
}) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        md: `repeat(${columns}, minmax(0, 1fr))`,
      },
      ...((sx as object) || {}),
    }}
  >
    {children}
  </Box>
);

/**
 * Rzad kart obok siebie od md; na telefonie jedna kolumna.
 * `template` to kolumny dla md, np. '2fr 1fr'.
 */
export const StatsRow: React.FC<{ template?: string; children: React.ReactNode; sx?: SxProps<Theme> }> = ({
  template = '1fr 1fr',
  children,
  sx,
}) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        md: template
          .split(' ')
          .map((c) => `minmax(0, ${c})`)
          .join(' '),
      },
      alignItems: 'stretch',
      ...((sx as object) || {}),
    }}
  >
    {children}
  </Box>
);

/** Kafelek statystyki — zgodny wstecz; w srodku wspolny `StatTile` (bez gradientow). */
export const StatCard: React.FC<{
  label: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  unit?: string;
  /** Nieuzywany — zostawiony dla zgodnosci wstecz. */
  gradient?: string;
  hint?: React.ReactNode;
}> = ({ label, value, change, changeLabel = 'niż poprzednio', unit = '', hint }) => (
  <StatTile
    label={label}
    value={value}
    unit={unit || undefined}
    change={change}
    changeLabel={change !== undefined ? changeLabel : undefined}
    hint={hint}
  />
);

export const SectionCard: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  padding?: 'normal' | 'none';
  children: React.ReactNode;
  sx?: object;
}> = ({ title, subtitle, action, padding, children, sx }) => (
  <UiSectionCard
    title={title}
    subtitle={subtitle}
    action={action}
    padding={padding}
    sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }}
    contentSx={{ flex: 1, minWidth: 0 }}
  >
    {children}
  </UiSectionCard>
);

/** Pusty wykres lub lista wewnatrz karty. */
export const EmptyHint: React.FC<{ children: React.ReactNode; description?: React.ReactNode; icon?: React.ReactNode }> = ({
  children,
  description,
  icon,
}) => (
  <Box sx={{ py: { xs: 3, md: 4 }, px: 1, textAlign: 'center' }}>
    {icon && (
      <Box
        sx={{
          width: 40,
          height: 40,
          mx: 'auto',
          mb: 1.25,
          borderRadius: '50%',
          bgcolor: tokens.plumSofter,
          color: tokens.plumLight,
          display: 'grid',
          placeItems: 'center',
          '& .MuiSvgIcon-root': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography component="div" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: tokens.inkSoft, maxWidth: '46ch', mx: 'auto' }}>
      {children}
    </Typography>
    {description && (
      <Typography component="div" sx={{ fontSize: '0.875rem', color: tokens.muted, mt: 0.5, maxWidth: '46ch', mx: 'auto' }}>
        {description}
      </Typography>
    )}
  </Box>
);

// Ranking: etykieta, pasek udzialu i wartosc na koncu wiersza.
export const RankList: React.FC<{
  items: { id: string; label: string; sub?: string; value: string; ratio: number }[];
  color?: string;
}> = ({ items, color = tokens.plum }) => (
  <Stack component="ol" gap={1.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {items.map((it, idx) => (
      <Box component="li" key={it.id}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1.5} mb={0.75}>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={it.label}
          >
            <Box component="span" sx={{ color: tokens.muted, mr: 1, fontVariantNumeric: 'tabular-nums' }}>
              {idx + 1}.
            </Box>
            {it.label}
          </Typography>
          <Stack direction="row" gap={1.25} alignItems="baseline" flexShrink={0}>
            {it.sub && (
              <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {it.sub}
              </Typography>
            )}
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {it.value}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{ height: 6, borderRadius: 99, bgcolor: tokens.plumSoft, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${Math.max(2, Math.min(100, it.ratio * 100))}%`, bgcolor: color, borderRadius: 99 }} />
        </Box>
      </Box>
    ))}
  </Stack>
);

/** Przelacznik miary wykresu — przychod i liczba wizyt maja rozne skale, wiec nie dzielimy jednej osi. */
export const MetricToggle: React.FC<{ value: 'revenue' | 'bookings'; onChange: (value: 'revenue' | 'bookings') => void }> = ({
  value,
  onChange,
}) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={value}
    onChange={(_e, v: 'revenue' | 'bookings' | null) => v && onChange(v)}
    aria-label="Miara na wykresie"
  >
    <ToggleButton value="revenue">Przychód</ToggleButton>
    <ToggleButton value="bookings">Wizyty</ToggleButton>
  </ToggleButtonGroup>
);

// Shared tooltip formatter for recharts (currency).
export const currencyTooltip = (value: number) => formatPLN(value, true);

// Fetch-on-deps hook used by every stats tab.
export function useAsyncData<T>(fetcher: () => Promise<T>, deps: React.DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetcher()
      .then((res) => {
        if (active) setData(res);
      })
      .catch(() => {
        if (active) setError('Nie udało się załadować statystyk. Odśwież stronę albo spróbuj ponownie za chwilę.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, deps);

  return { data, loading, error };
}

export const StatsLoader: React.FC<{
  loading: boolean;
  error: string;
  hasData: boolean;
  children: React.ReactNode;
  skeleton?: 'page' | 'tiles' | 'table' | 'cards' | 'block';
}> = ({ loading, error, hasData, children, skeleton = 'page' }) => {
  if (loading && !hasData) return <LoadingState variant={skeleton} />;
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }
  // Przy ponownym pobieraniu zostaje poprzedni widok, lekko przygaszony — bez skakania ukladu.
  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s ease' }} aria-busy={loading || undefined}>
      {children}
    </Box>
  );
};
